import { useMergeBomIntoSnapshot } from "@features/circuits/hooks/useCircuitIr";
import type { BomMergeResponse } from "@models/circuit-ir";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ConflictIcon from "@mui/icons-material/CompareArrowsRounded";
import GhostIcon from "@mui/icons-material/HelpOutlineRounded";
import UploadIcon from "@mui/icons-material/UploadFileRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";

interface BomMergeDialogProps {
  open: boolean;
  /** The snapshot the BOM merges into. The merge produces a NEW snapshot
   *  whose id is returned in `BomMergeResponse.snapshot_id`. */
  snapshotId: string | null;
  onClose: () => void;
  /**
   * Called once the merge succeeds — typically navigates the parent to
   * the new snapshot (e.g. updates the Drafts panel selection or routes
   * to a snapshot-specific BOM view).
   */
  onMerged?: (report: BomMergeResponse) => void;
}

/**
 * BOM CSV merge dialog (IR plan §9). Two-step UX:
 *
 *   1. Upload CSV → "Run merge" → backend produces a NEW snapshot and
 *      returns the report (matched / sch_only / bom_only / conflicts /
 *      duplicates).
 *   2. The user reviews the report and either:
 *      - "Apply this snapshot" → fires `onMerged`, the parent navigates
 *        / persists the new snapshot.
 *      - "Discard" → closes without firing `onMerged`. The new snapshot
 *        still exists server-side (the merge is reversible by reverting
 *        to `parent_snapshot_id`) but the user's working buffer is
 *        unaffected.
 *
 * Previously the dialog fired `onMerged` automatically on successful
 * merge, which gave the user no chance to inspect the conflicts before
 * committing. With a backend `POST /circuits/.../bom/preview` endpoint
 * we could make the first step truly read-only, but until then this is
 * close enough — the report carries all the same data either way.
 */
export function BomMergeDialog({
  open,
  snapshotId,
  onClose,
  onMerged,
}: BomMergeDialogProps) {
  // Body mounts/unmounts with ``open`` (MUI Dialog default) so file
  // + report state reset naturally on each open — no reset useEffect.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <BomMergeDialogBody
        snapshotId={snapshotId}
        onClose={onClose}
        onMerged={onMerged}
      />
    </Dialog>
  );
}

interface BomMergeDialogBodyProps {
  snapshotId: string | null;
  onClose: () => void;
  onMerged?: (report: BomMergeResponse) => void;
}

function BomMergeDialogBody({
  snapshotId,
  onClose,
  onMerged,
}: BomMergeDialogBodyProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const merge = useMergeBomIntoSnapshot();
  const [report, setReport] = useState<BomMergeResponse | null>(null);

  const canMerge = !!snapshotId && !!csvFile && !merge.isPending;

  function handleMerge() {
    if (!snapshotId || !csvFile) return;
    merge.mutate(
      { snapshotId, csv: csvFile },
      {
        onSuccess: (data) => {
          // Stash the report and let the user decide. `onMerged` fires
          // only on explicit confirmation below.
          setReport(data);
        },
      },
    );
  }

  function handleApply() {
    if (report) onMerged?.(report);
    onClose();
  }

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        {report ? "Review merge result" : "Merge BOM"}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {report
            ? "Inspect the merge before applying. Discarding leaves your current snapshot untouched."
            : "Refdes-keyed merge — BOM wins for procurement (MPN, manufacturer, supplier part numbers); schematic wins for connectivity (refdes, footprint, value, kind)."}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {!report && (
          <Stack spacing={2}>
            <Box
              component="button"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              sx={(t) => ({
                p: 3,
                bgcolor: "surface.raised",
                border: "1px dashed",
                borderColor: csvFile
                  ? t.palette.primary.main
                  : "border.default",
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "center",
                color: "text.secondary",
                width: "100%",
              })}
            >
              <UploadIcon
                fontSize="large"
                sx={{ color: "primary.main", mb: 1 }}
              />
              <Typography variant="body2">
                {csvFile ? csvFile.name : "Drop or click to select a BOM CSV"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                KiCad / EasyEDA / JLCPCB / generic CSV — column synonyms are
                auto-detected.
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
            />
            {merge.isPending && <LinearProgress />}
            {merge.error && (
              <Alert severity="error">
                {(merge.error as Error).message ??
                  "Merge failed — check the CSV format."}
              </Alert>
            )}
          </Stack>
        )}

        {report && <BomMergeReportPanel report={report} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!report && (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canMerge}
              onClick={handleMerge}
            >
              {merge.isPending ? "Running merge…" : "Run merge"}
            </Button>
          </>
        )}
        {report && (
          <>
            <Button onClick={onClose} color="inherit">
              Discard
            </Button>
            <Button variant="contained" onClick={handleApply}>
              Apply this snapshot
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function BomMergeReportPanel({ report }: { report: BomMergeResponse }) {
  const totals = {
    matched: report.matched.length,
    bomOnly: report.bom_only.length,
    schOnly: report.sch_only.length,
    conflicts: report.conflicts.length,
    duplicates: report.duplicate_refdes_in_bom.length,
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          icon={<CheckCircleIcon fontSize="small" />}
          label={`${totals.matched} matched`}
          color="success"
          variant="outlined"
        />
        {totals.conflicts > 0 && (
          <Chip
            icon={<ConflictIcon fontSize="small" />}
            label={`${totals.conflicts} conflict${totals.conflicts === 1 ? "" : "s"}`}
            color="warning"
            variant="outlined"
          />
        )}
        {totals.bomOnly > 0 && (
          <Chip
            icon={<GhostIcon fontSize="small" />}
            label={`${totals.bomOnly} BOM-only`}
            color="default"
            variant="outlined"
          />
        )}
        {totals.schOnly > 0 && (
          <Chip
            icon={<GhostIcon fontSize="small" />}
            label={`${totals.schOnly} schematic-only`}
            color="default"
            variant="outlined"
          />
        )}
        {totals.duplicates > 0 && (
          <Chip
            label={`${totals.duplicates} duplicate refdes`}
            color="error"
            variant="outlined"
          />
        )}
      </Stack>

      <ColumnMappingTable mapping={report.column_mapping} />

      {totals.conflicts > 0 && (
        <Section title="Resolved field conflicts">
          <Typography variant="caption" color="text.secondary">
            BOM precedence applied; previous schematic value preserved in
            history.
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Refdes</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Schematic</TableCell>
                <TableCell>BOM</TableCell>
                <TableCell>Kept</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.conflicts.map((c, i) => (
                <TableRow key={`${c.refdes}-${c.field}-${i}`}>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {c.refdes}
                  </TableCell>
                  <TableCell>{c.field}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {fmt(c.ir_value)}
                  </TableCell>
                  <TableCell>{fmt(c.bom_value)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {fmt(c.resolved)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {totals.bomOnly > 0 && (
        <Section title={`BOM-only refdes (${totals.bomOnly})`}>
          <RefdesChips items={report.bom_only} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            These refdes appear in the BOM but not the schematic — likely stale
            BOM rows or a manual designator typo. Not added to the IR.
          </Typography>
        </Section>
      )}

      {totals.schOnly > 0 && (
        <Section title={`Schematic-only refdes (${totals.schOnly})`}>
          <RefdesChips items={report.sch_only} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Schematic parts with no procurement record. Often DNP — verify.
          </Typography>
        </Section>
      )}

      {totals.duplicates > 0 && (
        <Alert severity="error">
          The BOM has duplicate designators (
          {report.duplicate_refdes_in_bom.join(", ")}). Only one row per refdes
          is kept; clean up the source CSV.
        </Alert>
      )}
    </Stack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {children}
    </Box>
  );
}

function ColumnMappingTable({ mapping }: { mapping: Record<string, string> }) {
  const rows = Object.entries(mapping);
  if (rows.length === 0) return null;
  return (
    <Section title="Column mapping (inferred)">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>CSV column</TableCell>
            <TableCell>IR field</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([csvCol, irField]) => (
            <TableRow key={csvCol}>
              <TableCell sx={{ fontFamily: "monospace" }}>{csvCol}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{irField}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
}

function RefdesChips({ items }: { items: string[] }) {
  const visible = items.slice(0, 30);
  const overflow = items.length - visible.length;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
      {visible.map((r) => (
        <Chip
          key={r}
          size="small"
          label={r}
          variant="outlined"
          sx={{ fontFamily: "monospace" }}
        />
      ))}
      {overflow > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: "center" }}
        >
          +{overflow} more
        </Typography>
      )}
    </Stack>
  );
}

function fmt(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value || "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
